import { ArrowRight, Check, Copy, Mic, Pencil, Upload } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "@src/shared/lib/utils";

const LOOP_ITEMS = [
  "A1c drawn today",
  "Retinal screening ordered",
  "Lisinopril increased",
  "Continue statin, recheck CK",
];

const LOOP_CODES = [
  { code: "E11.9", desc: "Type 2 diabetes", kind: "ICD-10" },
  { code: "3051F", desc: "A1c ordered", kind: "CPT-II" },
  { code: "2022F", desc: "Dilated retinal eval", kind: "CPT-II" },
  { code: "I10", desc: "Essential hypertension", kind: "ICD-10" },
];

function Wave({ big = false }: { big?: boolean }) {
  const heights = big
    ? [12, 22, 14, 26, 10, 20, 16, 24, 11, 18, 13, 22]
    : [14, 20, 11, 18, 9, 16, 12];

  return (
    <div className="flex items-center gap-[3px]" aria-hidden>
      {heights.map((height, index) => (
        <span
          className="w-[3px] animate-pulse rounded-sm bg-primary"
          key={`${height}-${index}`}
          style={{ height }}
        />
      ))}
    </div>
  );
}

export function LoopPreviewStep() {
  const [beat, setBeat] = useState(0);
  const [input, setInput] = useState(0);
  const [checked, setChecked] = useState(0);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    if (beat === 0) {
      setInput(0);
      timers.push(setTimeout(() => setInput(1), 1500));
      timers.push(setTimeout(() => setInput(2), 3000));
      timers.push(setTimeout(() => setBeat(1), 4500));
    } else if (beat === 1) {
      setChecked(0);
      LOOP_ITEMS.forEach((_, index) => {
        timers.push(setTimeout(() => setChecked(index + 1), 900 + index * 700));
      });
      timers.push(setTimeout(() => setBeat(2), 4300));
    } else {
      setTab(0);
      timers.push(setTimeout(() => setTab(1), 1900));
      timers.push(setTimeout(() => setBeat(0), 4200));
    }

    return () => timers.forEach(clearTimeout);
  }, [beat]);

  const beats = [
    { short: "Prep", chrome: "prep - build the agenda" },
    { short: "Visit", chrome: "visit - listening" },
    { short: "Done", chrome: "done - note + codes" },
  ];

  return (
    <div className="w-full max-w-[486px]">
      <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-[0_18px_50px_rgba(20,23,26,0.10)]">
        <div className="flex items-center gap-[7px] border-b border-border bg-muted/50 px-[13px] py-2.5">
          <span className="size-2 rounded-full bg-border" aria-hidden />
          <span className="size-2 rounded-full bg-border" aria-hidden />
          <span className="size-2 rounded-full bg-border" aria-hidden />
          <span className="ml-1.5 inline-flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
            {beat === 1 ? (
              <span className="size-1.5 animate-pulse rounded-full bg-destructive" />
            ) : null}
            {beats[beat].chrome}
          </span>
        </div>

        <div className="min-h-[200px] p-4 text-left">
          {beat === 0 ? (
            <div className="animate-in fade-in duration-300">
              <div className="mb-[13px] flex gap-1">
                {[
                  { icon: Pencil, label: "Type" },
                  { icon: Mic, label: "Speak" },
                  { icon: Upload, label: "Upload" },
                ].map((item, index) => (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-[7px] px-[9px] py-[5px] text-[11.5px] font-medium text-muted-foreground transition-colors",
                      input === index &&
                        "bg-muted font-semibold text-foreground",
                    )}
                    key={item.label}
                  >
                    <item.icon className="size-[13px]" aria-hidden />
                    {item.label}
                  </span>
                ))}
              </div>
              {input === 0 ? (
                <div className="whitespace-pre-line rounded-lg border border-border bg-muted/50 px-3 py-2.5 font-mono text-[11.5px] leading-[1.7] text-foreground">
                  A1c due{"\n"}Diabetic eye exam{"\n"}BP creeping up{"\n"}
                  Recheck statin
                  <span className="animate-pulse text-primary">|</span>
                </div>
              ) : null}
              {input === 1 ? (
                <div>
                  <div className="mb-2.5 flex items-center gap-2">
                    <span className="flex size-7 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Mic className="size-[15px]" aria-hidden />
                    </span>
                    <Wave />
                    <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                      listening
                    </span>
                  </div>
                  <p className="m-0 text-[13px] leading-[1.6] text-foreground">
                    &quot;
                    <strong className="text-primary">A1c</strong> due,{" "}
                    <strong className="text-primary">eye exam</strong>,{" "}
                    <strong className="text-primary">BP</strong> creeping up,
                    recheck the <strong className="text-primary">statin</strong>
                    .&quot;
                  </p>
                </div>
              ) : null}
              {input === 2 ? (
                <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed border-primary/50 bg-primary/10 px-3 py-[18px]">
                  <Upload className="size-[18px] text-primary" aria-hidden />
                  <span className="font-mono text-[11px] text-foreground">
                    care-gap-list.pdf
                  </span>
                  <span className="font-mono text-[9.5px] tracking-[0.3px] text-muted-foreground">
                    4 gaps found
                  </span>
                </div>
              ) : null}
              <p className="mt-[13px] font-mono text-[10px] tracking-[0.3px] text-muted-foreground">
                - becomes your visit agenda
              </p>
            </div>
          ) : null}

          {beat === 1 ? (
            <div className="animate-in fade-in duration-300">
              <div className="mb-[13px] flex items-center gap-2">
                <span className="flex size-[26px] items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Mic className="size-3.5" aria-hidden />
                </span>
                <Wave big />
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                  scribing
                </span>
              </div>
              <p className="mb-2.5 font-mono text-[10px] uppercase tracking-[0.4px] text-muted-foreground">
                Agenda - closes as you talk
              </p>
              <div className="flex flex-col gap-[7px]">
                {LOOP_ITEMS.map((item, index) => {
                  const done = index < checked;

                  return (
                    <div className="flex items-center gap-2" key={item}>
                      <span
                        className={cn(
                          "flex size-[17px] shrink-0 items-center justify-center rounded-full border border-border text-success-foreground",
                          done && "border-success bg-success",
                        )}
                      >
                        {done ? (
                          <Check className="size-2.5" strokeWidth={3} />
                        ) : null}
                      </span>
                      <span
                        className={cn(
                          "flex-1 text-[12.5px] text-muted-foreground transition-colors",
                          done && "text-foreground",
                        )}
                      >
                        {item}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {beat === 2 ? (
            <div className="animate-in fade-in duration-300">
              <div className="mb-3 flex gap-1">
                {["Note", "Codes"].map((item, index) => (
                  <span
                    className={cn(
                      "rounded-[7px] px-[11px] py-[5px] text-[11.5px] font-medium text-muted-foreground",
                      tab === index && "bg-muted font-semibold text-foreground",
                    )}
                    key={item}
                  >
                    {item}
                  </span>
                ))}
              </div>
              {tab === 0 ? (
                <div className="rounded-[9px] border border-border bg-muted/50 px-[11px] py-2.5 text-xs leading-[1.55] text-foreground">
                  62 y/o F, T2DM + HTN. A1c ordered today. BP 148/90, lisinopril
                  increased to 20 mg. Retinal screening ordered. Statin
                  tolerated; recheck CK in 6 weeks.
                </div>
              ) : (
                <div className="flex flex-col gap-[5px]">
                  {LOOP_CODES.map((code) => (
                    <div
                      className="flex items-center gap-2 text-[11.5px]"
                      key={code.code}
                    >
                      <code className="min-w-[52px] rounded border border-border bg-muted px-1.5 py-0.5 text-center font-mono text-[11px] font-semibold text-foreground">
                        {code.code}
                      </code>
                      <span className="flex-1 text-muted-foreground">
                        {code.desc}
                      </span>
                      <span className="font-mono text-[9px] tracking-[0.3px] text-muted-foreground">
                        {code.kind}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 flex items-center gap-1.5 text-[11.5px] font-semibold text-primary">
                <Copy className="size-[13px]" aria-hidden />
                Copy {tab === 0 ? "note" : "codes"} into your chart
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-3.5 flex items-center justify-center gap-2">
        {beats.map((item, index) => (
          <div className="inline-flex items-center gap-2" key={item.short}>
            <span
              className={cn(
                "text-[11.5px] font-medium text-muted-foreground",
                beat === index && "font-semibold text-foreground",
              )}
            >
              {item.short}
            </span>
            {index < beats.length - 1 ? (
              <ArrowRight
                className="size-[11px] text-muted-foreground"
                aria-hidden
              />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
